import React, { useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { SchemaEmail } from '@axiom/types';
import {
  ApiError,
  Button,
  CondensedLarge,
  CondensedMedium,
  Form,
  Gutter,
  Input,
  Well,
  Grid,
  GridRow,
  GridColumn,
  SmallHeader,
  AxiomLogo,
  PageContext,
} from '@axiom/ui';

import sideImageUrl from '../../public/svg/fingerprint.svg?url';
import { AuthApi } from '../../api/auth-api';
import { AuthResetsApi } from '../../api/auth-resets-api';
import { DefaultLayout } from '../../layouts/DefaultLayout/DefaultLayout';
import { WindowUtil } from '../../utils/WindowUtil';
import { MfaUtil } from '../../utils/mfa-util';
import { PasswordStrengthMeter } from '../PasswordStrengthMeter/PasswordStrengthMeter';
import type { LoginContextType } from '../LoginContext/LoginContext';
import { LoginContext } from '../LoginContext/LoginContext';

import { ClientResetPasswordFormSchema } from './client-reset-password-form-schema';

const { getUrlWithRelayState } = WindowUtil;

export type ClientResetPasswordProps = {
  email: string;
  token?: string | null;
  mfaCodeRequired?: boolean;
  name?: string;
};

export const ClientResetPassword = ({
  email,
  token,
  mfaCodeRequired = false,
  name = 'CLIENT_RESET_PASSWORD',
}: ClientResetPasswordProps) => {
  const { setTitle } = useContext(PageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const loginScreen = getUrlWithRelayState('/', {
    location,
  });
  const { setLoginState } = useContext(LoginContext);

  useEffect(() => {
    if (!email || !SchemaEmail.safeParse(email).success) {
      void navigate(
        getUrlWithRelayState('/', {
          location,
        })
      );
    }
  }, [email, location, navigate]);

  useEffect(() => {
    setTitle('Axiom Law - Reset Password');
  }, [setTitle]);

  const handleRedirect = (
    loginState: Partial<LoginContextType['loginState']>,
    jwt?: string
  ) => {
    setLoginState(loginState);
    MfaUtil.handleNavigation(navigate, location, jwt);
  };

  return (
    <DefaultLayout sideImageUrl={sideImageUrl} name={name}>
      <Grid centered>
        <GridRow>
          <GridColumn largeScreenWidth={9} tabletWidth={9} mobileWidth={12}>
            <Gutter bottom="24px" className="text-align-center">
              <AxiomLogo width="230px" name="AXIOM_LOGO" />
            </Gutter>
            <Gutter bottom="24px">
              <SmallHeader name="PASSWORD_HEADER">Set a password</SmallHeader>
            </Gutter>
          </GridColumn>
        </GridRow>
        <GridRow>
          <GridColumn largeScreenWidth={9} tabletWidth={9} mobileWidth={12}>
            <Form
              name="RESET_PASSWORD_FORM"
              onSubmit={async (changedData, actions, { password }) => {
                try {
                  if (!token) {
                    // user is setting password for the first time
                    const { data: jwt } = await AuthApi.createAuth({
                      email,
                      password,
                      mfaCode: null,
                    });
                    handleRedirect({ email, password }, jwt);
                  } else if (mfaCodeRequired) {
                    // user requires MFA to reset password
                    await AuthResetsApi.createMfaCode(token);

                    handleRedirect({
                      email,
                      password,
                      resetPasswordToken: token,
                    });
                  } else {
                    // user does not require MFA to reset password
                    const { data: jwt } = await AuthResetsApi.updatePassword(
                      token,
                      {
                        password,
                        mfaCode: null,
                      }
                    );

                    handleRedirect({ email, password }, jwt);
                  }
                } catch (e) {
                  if (!(e instanceof ApiError)) throw e;
                  actions.setFieldError(
                    'password',
                    'An error occurred. Please try again or reach out to Axiom Answers for Clients.'
                  );
                }
              }}
              schema={ClientResetPasswordFormSchema}
              initialValues={{}}
            >
              {({ fireSubmit, values: { password } }) => (
                <>
                  <Gutter bottom="8px">
                    <Well name="EMAIL_WELL">
                      <CondensedLarge name="email">{email}</CondensedLarge>
                    </Well>
                  </Gutter>
                  <Gutter bottom="24px">
                    <Link to={loginScreen} data-test="DIFFERENT_ACCOUNT_LINK">
                      <CondensedMedium>
                        Not you? Sign in with a different account.
                      </CondensedMedium>
                    </Link>
                  </Gutter>
                  <Gutter bottom="16px">
                    <Input label="Password" name="password" />
                  </Gutter>
                  <Gutter bottom="16px">
                    <PasswordStrengthMeter
                      password={password}
                      name="PASSWORD_STRENGTH_METER"
                    />
                  </Gutter>
                  <Button fluid onClick={fireSubmit} name="set-password">
                    Set Password
                  </Button>
                </>
              )}
            </Form>
          </GridColumn>
        </GridRow>
      </Grid>
    </DefaultLayout>
  );
};
